import { DOCUMENT, inject, Injectable } from '@angular/core'
import { AdRewardService } from './ad-reward.service'

type AdBreakStatus =
	| 'viewed'
	| 'dismissed'
	| 'ignored'
	| 'timeout'
	| 'error'
	| 'other'
	| 'noAdPreloaded'
	| 'noAdAvailable'
	| 'frequencyCapped'
	| 'notReady'

interface AdBreakDoneInfo {
	breakStatus: AdBreakStatus
}

declare global {
	interface Window {
		adsbygoogle: unknown[]
	}
}

@Injectable({ providedIn: 'root' })
export class WebAdRewardService extends AdRewardService {
	private readonly document = inject(DOCUMENT)
	private pendingAdBreakTimeout?: ReturnType<typeof setTimeout>

	initialize(isProd: boolean) {
		// The Ad Placement API only serves real ads on domains authorized in the AdSense/Ad
		// Placement console - localhost and other unauthorized origins never get a fill. Loading
		// the script with `data-adbreak-test="on"` switches it to always-available test ads, so
		// local/dev builds can exercise the reward flow. This must be set on the script tag itself
		// before it loads, so the tag can't be static in index.html (shared by every environment) -
		// it's injected here instead, gated by the same production flag main.ts already uses to
		// tell local dev apart from every deployed environment (staging/preprod/prod share one build).
		//
		// This must run during prerendering too (no isServer guard), not just in the browser: the
		// landing page is prerendered to static HTML, and AdSense's site-verification crawl reads
		// that static output rather than executing JS. The existing-tag check then stops the browser
		// from injecting a second copy when it hydrates a page that already carries the prerendered one.
		if (
			!this.document.head.querySelector(
				'script[src^="https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js"]',
			)
		) {
			const script = this.document.createElement('script')
			script.async = true
			script.src = 'https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js'
			script.setAttribute('data-ad-client', 'ca-pub-2719893969171432')
			if (!isProd) {
				script.setAttribute('data-adbreak-test', 'on')
			}
			this.document.head.appendChild(script)
		}

		this.pushAdBreakConfig({ preloadAdBreaks: 'on' })
	}

	private pushAdBreakConfig(config: Record<string, unknown>) {
		const window = this.document.defaultView
		if (!window) {
			return
		}
		window.adsbygoogle = window.adsbygoogle || []
		window.adsbygoogle.push(config)
	}

	async requestAd() {
		if (!this.shopRepository.hasAdvertisementOffer() || this.shopRepository.rewardedVideos() >= 5) {
			this.adAvailable.set(false)
			return false
		}

		this.adAvailable.set(true)
		return true
	}

	stopAd() {
		clearTimeout(this.pendingAdBreakTimeout)
		this.showRewardedAd.set(false)
		this.videoIsPaused.set(false)
		this.hasError.set(false)
	}

	async playAd() {
		this.hasError.set(false)
		this.videoIsPaused.set(false)

		if (!(await this.requestAd())) {
			this.hasError.set(true)
			return
		}

		this.showRewardedAd.set(true)
		this.adrewardStarted$.emit()

		// `adBreakDone` is documented as always firing, but a genuine no-fill can leave it never
		// called at all (observed on staging: an empty 200 response from pagead2, no adBreakDone, no
		// error - the popup just hangs on its loader forever). Fall back to an error state ourselves
		// if the SDK hasn't reported back within a reasonable delay.
		this.pendingAdBreakTimeout = setTimeout(() => {
			this.showRewardedAd.set(false)
			this.hasError.set(true)
		}, 10_000)

		this.pushAdBreakConfig({
			type: 'reward',
			name: 'memory-key-reward',
			beforeReward: (showAdFn: () => void) => showAdFn(),
			adDismissed: () => this.showRewardedAd.set(false),
			adViewed: () => this.creditAdReward(),
			adBreakDone: (info: AdBreakDoneInfo) => {
				clearTimeout(this.pendingAdBreakTimeout)
				this.showRewardedAd.set(false)
				if (info.breakStatus !== 'viewed' && info.breakStatus !== 'dismissed') {
					this.hasError.set(true)
				}
			},
		})
	}

	resumeAd() {
		// The Ad Placement API has no pause/resume concept for rewarded ad breaks.
	}
}
